import { useRef, useEffect, useState } from "react"
import { Button } from "semantic-ui-react"
import { PhotoIcon, PlusIcon, UserCircleIcon, LinkIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import CodeMirror from '@uiw/react-codemirror'
import { markdown } from '@codemirror/lang-markdown'
import { config } from '../../config/env';

import MarkdownComponent from "../../components/markdowncomponent/MarkdownComponent"
import { getToken } from "../../services/auth"
import {ReactComponent as ThreeBoxes} from "../../images/boxes.svg"
import {ReactComponent as TwoColumns} from "../../images/columns.svg"

const MarkdownEditor = ({
    parent_id = null,
    images, setImages,
    isLoading = false, setIsLoading,
    getValues, setValue,
    previewMarkdown = false, setPreviewMarkdown, 
    register, errors,
    removeAttachedImageCallback
}) => {
    // DEFAULT VALUES
    const templates = {
        threeBoxesTemplate : 
            `<div class="BoxContainer">\n` +
            `\t<div>\n` +
            `\t\t<h3>TOPIC_1</h3>\n` +
            `\t\t<p>DESCRIPTION_1</p>\n` +
            `\t</div>\n` +
            `\t<div>\n` +
            `\t\t<h3>TOPIC_2</h3>\n` +
            `\t\t<p>DESCRIPTION_2</p>\n` +
            `\t</div>\n` +
            `\t<div>\n` +
            `\t\t<h3>TOPIC_3</h3>\n` +
            `\t\t<p>DESCRIPTION_3</p>\n` +
            `\t</div>\n` +
            `</div>`,

        twoColumnsTemplate : 
            `<div class="TwoColumnsContainer">\n` +
            `\t<h2>Title</h2>\n` +
            `\t<div class="Separator"></div>\n` +
            `\t<div>\n` +
            `\t\t<strong>TITLE_1</strong>\n` +
            `\t\t<p>DESCRIPTION_1</p>\n` +
            `\t\t<strong>TITLE_2</strong>\n` +
            `\t\t<p>DESCRIPTION_2</p>\n` +
            `\t\t<strong>TITLE_3</strong>\n` +
            `\t\t<p>DESCRIPTION_3</p>\n` +
            `\t\t<strong>TITLE_4</strong>\n` +
            `\t\t<p>DESCRIPTION_4</p>\n` +
            `\t\t<strong>TITLE_5</strong>\n` +
            `\t\t<p>DESCRIPTION_5</p>\n` +
            `\t</div>\n` +
            `</div>`,

        authorCardTemplate :
            `<div class="authorCardContainer">\n` +
            `\t<div class="authorCard">\n` +
            `\t\t<img src="IMAGE_URL_LINK" class="authorPhoto" alt="Author Photo" />\n` +
            `\t\t<div>\n` +
            `\t\t\t<div class="authorName">AUTHOR_NAME</div>\n` +
            `\t\t\t<div class="authorRole">AUTHOR_ROLE</div>\n` +
            `\t\t\t<div class="authorCompany"><strong>Avanade</strong></div>\n` +
            `\t\t</div>\n` +
            `\t</div>\n` +
            `</div>`
    }

    // HOOKS
    const imageFile = useRef(null)
    const [tabs, setTabs] = useState([
        { name: 'Attached', current: true },
        { name: 'Global', current: false }
    ]);
    const globalImageFile = useRef(null)
    const [globalImages, setGlobalImages] = useState([]);

    useEffect(() => {
        const fetchGlobalImages = async () => {
            const response = await fetch(`${config.apiBaseUrl}/api/v1/admin/global/images/`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${await getToken()}`
                }
            });

            if (response.status !== 200) {
                console.error("Failed to fetch global images");
                return;
            }

            const resJson = await response.json();
            setGlobalImages(resJson.data.map((img) => ({
                id: img.id,
                filename: img.filename,
                url: img.url
            })));
        }
        fetchGlobalImages();
    }, [])
    
    // FUNCTION
    const removeImage = (removeImage) => {
        if (removeAttachedImageCallback) {
            removeAttachedImageCallback(parent_id, removeImage);
        }
        setImages((prevImages) => prevImages.filter((v) => v.id !== removeImage.id));
        let imageFilenames = getValues("image_filenames") || [];
        setValue("image_filenames", imageFilenames.filter((v) => v !== removeImage.filename));
        console.log("Image removed:", removeImage.id)
    }

    const removeGlobalImage = async (removeImage) => {
        const response = await fetch(`${config.apiBaseUrl}/api/v1/admin/global/images/${removeImage.id}/${removeImage.filename}/`, {
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${await getToken()}`
            }
        });

        if (response.status !== 200) {
            console.error("Failed to delete global image:", removeImage.id);
            return;
        }
        
        setGlobalImages((prevImages) => prevImages.filter((v) => v.id !== removeImage.id));
        console.log("Global Image removed:", removeImage.id)
    }

    const copyTemplate = (template) => {
        console.log('Copy Template')
        navigator.clipboard.writeText(templates[template])
            .then(() => {
                console.log(`${template} copied to clipboard`)
            })
            .catch((error) => {
                console.error("Failed to copy pre-layout:", error)
        })
    }

    const copyImageMarkdown = async (imageUrl) => {
        if (!imageUrl) return;
        await navigator.clipboard.writeText(`![image](${imageUrl})`);
    };

    const copyImageUrl = async (imageUrl) => {
        if (!imageUrl) return;
        await navigator.clipboard.writeText(imageUrl);
    }

    // HANDLERS
    const handleOnClickUploadImage = (e) => {
        imageFile.current.click()
    }

    const handleOnClickUploadGlobalImage = (e) => {
        globalImageFile.current.click()
    }

    const handleOnChangeUploadImage = async (event) => {
        const files = event.target.files;
        setIsLoading(true);
        if (files && files.length > 0) {
            const fileArray = Array.from(files);

            try {
                const uploadImages = await Promise.all(
                    fileArray.map(async (file) => {
                        const newResearchPaperFile = new FormData();
                        newResearchPaperFile.append("file", file);  
                        newResearchPaperFile.append("file_type", 1);
                        const response = await fetch(`${config.apiBaseUrl}/api/v1/admin/files/`, {
                            method: "POST",
                            body: newResearchPaperFile,
                            headers: {
                                "Authorization": `Bearer ${await getToken()}`
                            }
                        });
                        
                        const data = await response.json();
                        if (response.status === 200) {
                            let imageFilenames = getValues("image_filenames") || [];
                            setValue("image_filenames", [...imageFilenames, data.filename])
                            return {
                                id: data.id,
                                filename: data.filename,
                                url: data.url
                            };
                        }
                        return null;
                    })
                );
                setImages((prev) => {
                const newImages = [...prev, ...uploadImages.filter(Boolean)];
                // Update the form field to match the images list
                setValue("image_filenames", newImages.map((img) => img.filename));
                return newImages;
            });
            } catch (error) {
                console.error('Error uploading image:', error);
            } finally {
                setIsLoading(false);
            }
        }
    }

    const handleOnClickTogglePreview = (e) => {
        console.log('Toggle Preview')
        setPreviewMarkdown(!previewMarkdown)
    }

    const handleOnTabClick = (tabName) => {
        const updatedTabs = tabs.map((tab) => ({
            ...tab,
            current: tab.name === tabName
        }));
        setTabs(updatedTabs);
    }

    const handleOnChangeUploadGlobalImage = async (event) => {
        const files = event.target.files;
        setIsLoading(true);
        if (files && files.length > 0) {
            const fileArray = Array.from(files);

            try {
                await Promise.all(
                    fileArray.map(async (file) => {
                        const newResearchPaperFile = new FormData();
                        newResearchPaperFile.append("file", file);
                        newResearchPaperFile.append("file_type", 1);
                        const response = await fetch(`${config.apiBaseUrl}/api/v1/admin/global/images/`, {
                            method: "POST",
                            body: newResearchPaperFile,
                            headers: {
                                "Authorization": `Bearer ${await getToken()}`
                            }
                        });
                        
                        const data = await response.json();
                        if (response.status === 200) {
                            setGlobalImages((prev) => {
                                const newImages = [...prev, {
                                    id: data.id,
                                    filename: data.filename,
                                    url: data.url
                                }];
                                return newImages;
                            });
                        }
                        return null;
                    })
                );
            } catch (error) {
                console.error('Error uploading image:', error);
            } finally {
                setIsLoading(false);
            }
        }
    }

    // RENDER
    const renderMarkdownContentSection = () => {
        return (
            <div className="w-[85%] max-w-[85%] pr-8 relative">
                <div className="absolute right-[15px] top-[-4px] pr-5 flex gap-2 items-center">
                    <div className="p-[1.25px] rounded-md border border-gray-300 bg-gray-100 hover:bg-gray-300 shadow-md">
                        <ThreeBoxes className="shrink-0 cursor-pointer px-[2px]" width={25} height={25} onClick={() => copyTemplate("threeBoxesTemplate")} title="Copy three boxes layout"/>
                    </div>
                    <div className="p-[1.25px] rounded-md border border-gray-300 bg-gray-100 hover:bg-gray-300 shadow-md">
                        <TwoColumns className="shrink-0 cursor-pointer px-[2px]" width={25} height={25} onClick={() => copyTemplate("twoColumnsTemplate")} title="Copy two columns layout"/>
                    </div>
                    <div className="p-[1.25px] rounded-md border border-gray-300 bg-gray-100 hover:bg-gray-300 shadow-md">
                        <UserCircleIcon className="shrink-0 cursor-pointer px-[2px]" width={25} height={25} onClick={() => copyTemplate("authorCardTemplate")} title="Copy author card layout"/>
                    </div>
                    <div className={`cursor-pointer rounded-lg text-xs px-2 py-1 ${previewMarkdown ? "bg-blue-500 text-white" : "bg-white text-blue-500"}`} onClick={handleOnClickTogglePreview}>
                        Preview
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Markdown Content</label>
                    <CodeMirror
                        value={getValues("content") || ""}
                        height="518px"
                        extensions={[markdown()]}
                        onChange={(value) => setValue("content", value)}
                        className={`border border-gray-300 rounded-lg overflow-hidden ${previewMarkdown ? "hidden" : "block"}`}
                    />
                    {errors.content && <p className="text-red-500 text-sm mt-1">MarkdownContent is required</p>}
                    { previewMarkdown && (
                        <div className="h-[518px] border border-gray-300 rounded-lg overflow-y-scroll">
                            <MarkdownComponent markdownContent={getValues("content")} />
                        </div>
                    )}
                </div>
            </div>
        )
    }

    const renderTabsSection = () => {
        function classNames(...classes) {
           return classes.filter(Boolean).join(' ')
        }

        return (<>
            <div>
                <div className="grid grid-cols-1 sm:hidden">
                    {/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
                    <select
                    defaultValue={tabs.find((tab) => tab.current).name}
                    aria-label="Select a tab"
                    className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                    >
                    {tabs.map((tab) => (
                        <option key={tab.name}>{tab.name}</option>
                    ))}
                    </select>
                    <ChevronDownIcon
                    aria-hidden="true"
                    className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-500"
                    />
                </div>
                <div className="hidden sm:block">
                    <div className="border-b border-gray-200">
                    <nav aria-label="Tabs" className="-mb-px flex space-x-8">
                        {tabs.map((tab) => (
                        <button
                            key={tab.name}
                            onClick={(e) => { handleOnTabClick(tab.name) }}
                            aria-current={tab.current ? 'page' : undefined}
                            className={classNames(
                            tab.current
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700',
                            'flex whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium',
                            )}
                        >
                            {tab.name}
                            {tab.count ? (
                            <span
                                className={classNames(
                                tab.current ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-900',
                                'ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block',
                                )}
                            >
                                {tab.count}
                            </span>
                            ) : null}
                        </button>
                        ))}
                    </nav>
                    </div>
                </div>
            </div>
        </>)
    }
    const renderAttachedImagesSection = () => {
        return (<>
            <div className="mt-2">
                {!isLoading && 
                <div className="bg-gray-100 h-24 content-center hover:bg-gray-50 cursor-pointer" onClick={handleOnClickUploadImage}>
                    <input type="file"  accept="image/*" ref={imageFile} className="hidden" onChange={handleOnChangeUploadImage} multiple />
                    <PlusIcon aria-hidden="true" className="size-6 shrink-0 text-gray-500 mx-auto" />
                </div>}
            </div>
            <div className="mt-2">
                {images.length > 0 && (
                    <ul className="space-y-2">
                        {images.map((image, index) => (
                            <li key={index} className="relative group w-fit">
                                { !isLoading && <div className="absolute top-0 left-0 w-full flex justify-end gap-2 p-1 z-20 opacity-0 group-hover:opacity-100 transition">
                                    <Button onClick={() => copyImageUrl(image.url) } className="bg-blue-600 p-1 text-white text-xs rounded-md shadow hover:bg-blue-700 transition">
                                        <LinkIcon className="h-4 w-4 inline-block" />
                                    </Button>
                                    <Button onClick={() => copyImageMarkdown(image.url) } className="bg-blue-600 p-1 text-white text-xs rounded-md shadow hover:bg-blue-700 transition">
                                        <PhotoIcon className="h-4 w-4 inline-block" />
                                    </Button>
                                    <Button onClick={() => removeImage(image) } className="bg-red-600 p-1 text-white text-xs rounded-md shadow hover:bg-red-700 transition">
                                        <XMarkIcon className="h-4 w-4 inline-block" />
                                    </Button>
                                </div>}
                                <img src={image.url} alt={image.filename} className="rounded border-gray-200 mt-4 hover:border-2 hover:border-primary" />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </>)
    }

    const renderGlobalImagesSection = () => {
        return (<>
            <div className="mt-2">
                {!isLoading && 
                <div className="bg-gray-200 h-24 content-center hover:bg-gray-50 cursor-pointer" onClick={handleOnClickUploadGlobalImage}>
                    <input type="file"  accept="image/*" ref={globalImageFile} className="hidden" onChange={handleOnChangeUploadGlobalImage} multiple />
                    <PlusIcon aria-hidden="true" className="size-6 shrink-0 text-gray-500 mx-auto" />
                </div>}
            </div>
            <div className="mt-2">
                {globalImages.length > 0 && (
                    <ul className="space-y-2">
                        {globalImages.map((image, index) => (
                            <li key={index} className="relative group w-fit">
                                { !isLoading && <div className="absolute top-0 left-0 w-full flex justify-end gap-2 p-1 z-20 opacity-0 group-hover:opacity-100 transition">
                                    <Button onClick={() => copyImageUrl(image.url) } className="bg-blue-600 p-1 text-white text-xs rounded-md shadow hover:bg-blue-700 transition">
                                        <LinkIcon className="h-4 w-4 inline-block" />
                                    </Button>
                                    <Button onClick={() => copyImageMarkdown(image.url) } className="bg-blue-600 p-1 text-white text-xs rounded-md shadow hover:bg-blue-700 transition">
                                        <PhotoIcon className="h-4 w-4 inline-block" />
                                    </Button>
                                    <Button onClick={() => removeGlobalImage(image) } className="bg-red-600 p-1 text-white text-xs rounded-md shadow hover:bg-red-700 transition">
                                        <XMarkIcon className="h-4 w-4 inline-block" />
                                    </Button>
                                </div>}
                                <img src={image.url} alt={image.filename} className="rounded border-gray-200 mt-4 hover:border-2 hover:border-primary" />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </>)
    }

    const renderSelectedImagesSection = () => {
        const currentTab = tabs.find((tab) => tab.current).name;
        if (currentTab === 'Attached') {
            return renderAttachedImagesSection();
        } else if (currentTab === 'Global') {
            return renderGlobalImagesSection();
        }
    }

    const renderImagesSection = () => {
        return (
            <div className="w-[15%] h-[545px] overflow-y-scroll">
                { renderTabsSection() }
                { renderSelectedImagesSection() }
            </div>
        )
    }
    
    return (
        <div className="mt-3 flex">
            { renderMarkdownContentSection() }
            { renderImagesSection() }
        </div>
    )
}

export default MarkdownEditor;